import { useEffect, useMemo, useRef, useState } from "react";
import {
  Plug, Check, X, Loader2,
  Wifi, WifiOff, Plus, Zap, ShieldCheck, ExternalLink, Trash2, AlertTriangle, MessageSquare, Search, ChevronDown, ChevronUp
} from "lucide-react";
import { ConnectorIcon } from "./ConnectorIcon";
import { loadConnectorDraft, saveConnectorDraft, clearConnectorDraft } from "../../lib/connector-setup-draft";

// ═══════════════════════════════════════════════════════════════════════
// INTEGRATION HUB — REAL MCP
// ═══════════════════════════════════════════════════════════════════════
// Catalog loaded from /api/mcp/catalog (mcp_registry table — 25+ providers).
// Connections loaded from /api/mcp/connections (per-Agentic Operating System state).
// Click Connect → real OAuth flow via /api/oauth/:authProvider/start?provider=X.
// Provider redirects back to /api/oauth/:authProvider/callback, we store the token,
// redirect to /?screen=integrations&mcp_connected=<provider>, and refresh the list.
// ═══════════════════════════════════════════════════════════════════════

interface MCPProvider {
  id: string;
  provider: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  color: string;
  mcp_url: string;
  is_active: boolean;
  connectable?: boolean; // PHASE 3 — from /api/mcp/catalog; false → request connector path
  auth_provider?: string | null;
  oauth_start_path?: string | null;
  oauth_disconnect_path?: string | null;
  capabilities?: string[];
}

function authProviderForConnector(provider: string, tool?: MCPProvider | null): string | null {
  if (tool?.auth_provider) return tool.auth_provider;
  // Real Composio Google toolkits — fallback when a catalog entry lacks auth_provider.
  if (/^(gmail|googlecalendar|googledrive|googledocs|googlesheets)$/.test(provider)) return "composio";
  return null;
}

const RETURN_SCREEN = "integrations";

// Display-name overrides (UI only — the mcp_registry name / provider slug is
// untouched). Lead with what users search for; `short` keeps chat CTAs clean.
const PROVIDER_LABEL_OVERRIDES: Record<string, { full: string; short: string }> = {
  gmail: { full: "Gmail", short: "Gmail" },
  googlecalendar: { full: "Google Calendar", short: "Calendar" },
  googledrive: { full: "Google Drive", short: "Drive" },
  googledocs: { full: "Google Docs", short: "Docs" },
  googlesheets: { full: "Google Sheets", short: "Sheets" },
  googlemeet: { full: "Google Meet", short: "Meet" },
  microsoft_teams: { full: "Microsoft Teams", short: "Teams" },
  one_drive: { full: "OneDrive", short: "OneDrive" },
};
function providerLabel(provider: string, fallback: string): { full: string; short: string } {
  const o = PROVIDER_LABEL_OVERRIDES[provider];
  return { full: o?.full || fallback, short: o?.short || fallback };
}

// Featured connectors surface at the top of the Composio catalog so the apps
// users most often want are one click away instead of buried in the 500-app
// browse list. This is the "Tier 1" universal demo set — each is a real Composio
// toolkit that connects + exposes tools exactly like Gmail; its brand logo comes
// from the catalog automatically (logos.composio.dev), so featuring = surfacing
// the card, no icon work. Order = demo priority (comms/email/calendar → docs/
// files → CRM → payments → meetings/scheduling → accounting).
const FEATURED_COMPOSIO_PROVIDERS = [
  "gmail", "outlook", "googlecalendar", "slack", "microsoft_teams",
  "notion", "googledocs", "googledrive", "dropbox", "one_drive",
  "googlesheets", "airtable", "hubspot", "salesforce", "stripe",
  "square", "zoom", "googlemeet", "calendly", "freshbooks",
  // Cloud browser: lets the agent drive a real browser (navigate, click, fill,
  // scrape) for anything with no API. Composio-managed auth, so it connects
  // with no key from the user.
  "browser_tool",
];
function sortFeaturedFirst(list: MCPProvider[]): MCPProvider[] {
  const rank = (p: string) => {
    const i = FEATURED_COMPOSIO_PROVIDERS.indexOf(p);
    return i === -1 ? FEATURED_COMPOSIO_PROVIDERS.length : i;
  };
  return [...list].sort((a, b) => rank(a.provider) - rank(b.provider));
}

export interface Connection {
  id: string;
  provider: string;
  status: "connected" | "error" | "expired";
  gmail_ready?: boolean;          // B2 — false for connections predating the Gmail scope widening
  gmail_send_ready?: boolean;     // Launch light scope — sending works even when read/compose are gated off
  sheets_ready?: boolean;         // Live data import scope for connected sheet sources
  drive_ready?: boolean;          // Drive metadata search for finding named trackers/files
  drive_read_ready?: boolean;     // Drive file read scope for importing CSV trackers
  restricted_enabled?: boolean;   // false at launch → don't nag to approve gated inbox-read / Drive scopes
  last_synced_at: string | null;
  expires_at: string | null;
  created_at: string;
  config?: {
    base_url?: string;
    resource?: string;
    screen_id?: string;
    reso_failures?: number;
    last_sync_error?: string | null;
    agent_ready?: boolean;
    verified_for_agent?: boolean;
    agent_ready_at?: string | null;
    agent_ready_tool?: string | null;
  } | null;
}

export interface ConnectorRepairState {
  needsRepair: boolean;
  severity: "ok" | "warning" | "error";
  label: string;
  detail: string;
  actionLabel: string | null;
  missingCapabilities: string[];
}

interface LiveDataSource {
  id: string;
  provider: string;
  source_id: string;
  source_name?: string | null;
  range: string | null;
  row_count: number;
  screen_ids: string[];
  screen_labels: string[];
  last_imported_at: string | null;
  web_url?: string | null;
}

interface ConnectorReadinessRow {
  provider: string;
  label: string;
  connected: boolean;
  usable: boolean;
  scope_ready: boolean;
  tool_exposed: boolean;
  prompt_ready: boolean;
  smoke_tested: boolean;
  smoke_tool?: string | null;
  smoke_error?: string | null;
  verified_at?: string | null;
  tool_count: number;
  capabilities: string[];
  missing_steps: string[];
  failure_reason: string | null;
}

interface ConnectorReadinessReport {
  total_count: number;
  ready_count: number;
  missing_count: number;
  rows: ConnectorReadinessRow[];
}

type PendingAccessAction =
  | { kind: "disconnect"; provider: string; label: string }
  | { kind: "revoke_desktop"; permission: { id: string; label: string } };

const CATEGORY_LABELS: Record<string, string> = {
  crm: "CRM",
  finance: "Finance",
  communication: "Communication",
  storage: "Storage",
  analytics: "Analytics",
  productivity: "Productivity",
  marketing: "Marketing",
  social: "Social",
  support: "Support",
  development: "Development",
  automation: "Automation",
  custom: "Custom / Other",
};

const CATEGORY_ORDER = [
  "communication",
  "social",
  "crm",
  "marketing",
  "productivity",
  "storage",
  "finance",
  "development",
  "analytics",
  "support",
  "automation",
  "custom",
] as const;

const SHOW_CONNECTOR_CATALOG = true;
const NEEDS_SETUP_TTL_MS = 7 * 24 * 60 * 60 * 1000; // R3b — needs-setup marker TTL
// R3c — "Pro and up" gate (mirrors server isProOrUp). Fail-safe: a null/unknown
// plan is NOT pro, so a flaky /api/billing/usage fetch degrades to the upsell.
const CLIENT_PLAN_WEIGHT: Record<string, number> = { trial: 0, starter: 1, pro: 2, business: 3, enterprise: 4 };
const isProPlan = (p?: string | null) => (CLIENT_PLAN_WEIGHT[String(p || "")] ?? 0) >= 2;

// Phase 3 — bridge from the Architect's canonical tool slugs to whatever
// `tool.provider` / `tool.name` strings the mcp_registry actually uses.
// Hand-maintained; expand as catalog grows. Drift is a known follow-up.
const TOOL_ALIASES: Record<string, string[]> = {
  "google-drive": ["google-workspace", "google-drive", "gdrive"],
  "gmail": ["google-workspace", "gmail"],
  "microsoft-365": ["microsoft-365", "office-365", "m365"],
};

// connectorMissingCapabilities was removed: it had already been reduced to a
// stub returning [], which made the "Missing scopes" branch below unreachable.
// Per-capability scope inspection died with the native Google connector
// (2c37180) — readiness is now the agent-ready proof marker. `missingCapabilities`
// stays on ConnectorRepairState as a stable [] so the render path keeps its shape.

export function connectorRepairState(connection: Pick<Connection, "provider" | "status" | "gmail_ready" | "sheets_ready" | "drive_ready" | "drive_read_ready" | "restricted_enabled" | "config">): ConnectorRepairState {
  if (connection.provider === "reso" && connection.status === "error") {
    const reason = connection.config?.last_sync_error;
    return {
      needsRepair: true,
      severity: "error",
      label: "Needs attention",
      detail: reason
        ? `RESO feed stopped syncing: ${reason}. Reconnect the feed.`
        : "The RESO feed stopped syncing. Reconnect it.",
      actionLabel: "Reconnect",
      missingCapabilities: [],
    };
  }
  if (connection.status === "error") {
    return {
      needsRepair: true,
      severity: "error",
      label: "Needs repair",
      detail: "This connection returned an error, so agents should not depend on it until you approve a fresh connection.",
      actionLabel: "Repair access",
      missingCapabilities: [],
    };
  }

  if (connection.status === "expired") {
    return {
      needsRepair: true,
      severity: "warning",
      label: "Expired",
      detail: "The token expired. Reconnect it so agents can keep reading the approved live data source.",
      actionLabel: "Reconnect",
      missingCapabilities: [],
    };
  }

  const agentReady = Boolean(
    connection.config?.agent_ready_at
    || connection.config?.agent_ready === true
    || connection.config?.verified_for_agent === true,
  );
  if (!agentReady && connection.provider !== "resend_email" && connection.provider !== "reso") {
    return {
      needsRepair: true,
      severity: "warning",
      label: "Proof needed",
      detail: "The account exists, but Composio has not yet completed a successful agent tool call.",
      actionLabel: "Prove access",
      missingCapabilities: [],
    };
  }

  return {
    needsRepair: false,
    severity: "ok",
    label: "Ready",
    detail: "Connected and ready for approved agent work.",
    actionLabel: null,
    missingCapabilities: [],
  };
}

function normalizeSlug(s: string): string {
  return String(s || "").toLowerCase().replace(/[_\s]+/g, "-");
}

function isMentioned(tool: MCPProvider, mentionedNorms: Set<string>): boolean {
  if (mentionedNorms.size === 0) return false;
  const provNorm = normalizeSlug(tool.provider);
  const nameNorm = normalizeSlug(tool.name);
  if (mentionedNorms.has(provNorm) || mentionedNorms.has(nameNorm)) return true;
  for (const slug of mentionedNorms) {
    const aliases = TOOL_ALIASES[slug];
    if (aliases?.some(a => normalizeSlug(a) === provNorm || normalizeSlug(a) === nameNorm)) {
      return true;
    }
  }
  return false;
}

export function buildConnectorRequestPrompt(tool: Pick<MCPProvider, "name" | "category">): string {
  return [
    `I need the ${tool.name} connector for the Agentic Operating System.`,
    `Category: ${CATEGORY_LABELS[tool.category] || tool.category}.`,
    "Research whether this connector is necessary for my workflow, identify the smallest safe scopes or API permissions, and create a Decision before anything is deployed.",
  ].join("\n");
}

export function IntegrationHub({
  workspaceId,
  toolsMentioned,
  onAskOperator,
  onAskArchitect,
  screens,
}: {
  workspaceId: string;
  toolsMentioned?: string[];
  onAskOperator?: (prompt: string) => void;
  onAskArchitect?: (prompt: string) => void;
  screens?: Array<{ screen_id: string; label?: string }>;
}) {
  const [catalog, setCatalog] = useState<MCPProvider[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [liveSources, setLiveSources] = useState<LiveDataSource[]>([]);
  const [readiness, setReadiness] = useState<ConnectorReadinessReport | null>(null);
  const [smokingProvider, setSmokingProvider] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  // SF-2 — distinguish "connectors failed to load" from a genuinely-empty
  // catalog. On a fetch failure the list would otherwise read as "no connectors".
  const [loadError, setLoadError] = useState(false);
  const [permissionTool, setPermissionTool] = useState<MCPProvider | null>(null);
  const [pendingAccessAction, setPendingAccessAction] = useState<PendingAccessAction | null>(null);
  const [disconnectingProvider, setDisconnectingProvider] = useState<string | null>(null);
  const [revokingPermissionId, setRevokingPermissionId] = useState<string | null>(null);
  const [resoModalOpen, setResoModalOpen] = useState(false);
  const [resoBaseUrl, setResoBaseUrl] = useState("");
  const [resoToken, setResoToken] = useState("");
  const [resoScreenId, setResoScreenId] = useState("");
  const [resoSubmitting, setResoSubmitting] = useState(false);
  const [resoMapped, setResoMapped] = useState<string[]>([]);
  const [resoUnmapped, setResoUnmapped] = useState<string[]>([]);
  const [resoMapError, setResoMapError] = useState<string | null>(null);
  // Connector-setup draft (Jacob #7): rehydrate the in-progress builder from
  // localStorage so leaving/reloading the hub no longer wipes what the user was
  // setting up. Lazy-init reads the per-OS draft once on mount (no rehydrate
  // race with the debounced save effect below).
  const [appDraft, setAppDraft] = useState<string>(() => loadConnectorDraft(workspaceId)?.appDraft || "");
  const [acceptedApps, setAcceptedApps] = useState<string[]>(() => loadConnectorDraft(workspaceId)?.acceptedApps || []);
  // "Browse apps" = the Composio catalog (loaded once via /api/mcp/catalog,
  // cached server-side) filtered CLIENT-SIDE by the search box — instant search,
  // no per-keystroke API call.
  const [composioResults, setComposioResults] = useState<MCPProvider[]>([]);
  // A failed Composio catalog call surfaces here as a visible banner,
  // never a silent empty "No apps to show."
  const [composioError, setComposioError] = useState<string | null>(null);
  const [showMoreCategories, setShowMoreCategories] = useState(false);
  // R3b — feature B: the app just connected (arms the "Talk to your agent" CTA).
  const [justConnected, setJustConnected] = useState<string | null>(null);
  const [connectingProvider, setConnectingProvider] = useState<string | null>(null);
  const connectPopupRef = useRef<Window | null>(null);
  const connectTimeoutRef = useRef<number | null>(null);
  const connectInFlightRef = useRef<string | null>(null);
  // R3c — feature A: Pro-gated "Submit request". plan drives the gate (null →
  // upsell, the safe direction). Submit form + Stripe-checkout state.
  const [plan, setPlan] = useState<string | null>(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitApp, setSubmitApp] = useState("");
  const [submitNote, setSubmitNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitDone, setSubmitDone] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  // R3b — apps whose Composio managed-auth isn't available: rendered as a
  // disabled "Needs setup" tile. Discover-on-click, persisted per-workspace with
  // a 7-day TTL so a temporary gap self-clears and re-checks (never a permanent
  // false-negative).
  const [needsSetup, setNeedsSetup] = useState<Set<string>>(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(`sloe.needs_setup.${workspaceId}`) || "{}");
      const now = Date.now(); const valid: Record<string, number> = {};
      for (const [p, exp] of Object.entries(raw)) if (typeof exp === "number" && exp > now) valid[p] = exp;
      localStorage.setItem(`sloe.needs_setup.${workspaceId}`, JSON.stringify(valid));
      return new Set(Object.keys(valid));
    } catch { return new Set(); }
  });

  useEffect(() => {
    // Detect OAuth return params FIRST so loadAll's stale-cache clear can spare a
    // provider that this very load is (legitimately) re-marking needs_setup.
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("mcp_connected");
    const errored   = params.get("mcp_error");
    const justNeedsSetup = errored === "needs_setup" ? (params.get("provider") || "") : "";
    let cancelled = false;

    if (connected) {
      // Optimistically reflect the new connection immediately; we'll reconcile
      // with the DB row below so the page doesn't look broken while the sync
      // settles.
      setJustConnected(connected);
    }

    const refreshAfterConnect = async (attempt = 0) => {
      const currentConnections = await loadAll(justNeedsSetup || undefined);
      if (cancelled || !connected) return;
      const found = currentConnections.some((conn: { provider: string; status?: string }) => conn.provider === connected && conn.status === "connected");
      if (found) {
        await loadReadiness();
        setStatusMessage(`✓ ${connected} connected successfully`);
        setJustConnected(connected); // R3b feature B — arm the "Talk to your agent" CTA
        // Setup completed — the draft has served its purpose; clear it + reset the
        // in-progress builder so a stale list doesn't linger after connecting.
        clearConnectorDraft(workspaceId);
        setAcceptedApps([]);
        setAppDraft("");
        window.history.replaceState({}, "", window.location.pathname);
        setTimeout(() => setStatusMessage(null), 4000);
        return;
      }
      if (attempt < 12) {
        window.setTimeout(() => {
          if (!cancelled) void refreshAfterConnect(attempt + 1);
        }, 750);
        return;
      }
      setStatusMessage(`⚠ ${connected || "That app"} connected in the browser, but the saved connection has not appeared yet. Refresh or reconnect if needed.`);
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(() => setStatusMessage(null), 8000);
    };
    void refreshAfterConnect();
    if (!connected) {
      setJustConnected(null);
    }
    if (errored === "needs_setup") {
      const p = params.get("provider") || "";
      if (p) markNeedsSetup(p); // R3b — persist the disabled "Needs setup" tile
      setStatusMessage(`⚠ ${p || "That app"} needs setup — no managed auth available yet`);
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(() => setStatusMessage(null), 6000);
    } else if (errored) {
      // Transient/unexpected connect failure (link_failed,
      // connection_failed, …) — NOT the honest "no managed auth" case, so we do
      // NOT persist a Needs-setup tile (that would falsely grey a working app on
      // a blip). Show a clear, retryable message; the card stays on Connect.
      const p = params.get("provider") || "";
      setStatusMessage(`✗ Couldn't connect${p ? ` ${p}` : ""} — please try again.`);
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(() => setStatusMessage(null), 6000);
    }
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  useEffect(() => {
    const clearConnectFlow = () => {
      if (connectTimeoutRef.current) {
        window.clearTimeout(connectTimeoutRef.current);
        connectTimeoutRef.current = null;
      }
      try {
        if (connectPopupRef.current && !connectPopupRef.current.closed) {
          connectPopupRef.current.close();
        }
      } catch {}
      connectPopupRef.current = null;
      connectInFlightRef.current = null;
      setConnectingProvider(null);
    };

    const syncPopupState = () => {
      if (connectPopupRef.current && !connectPopupRef.current.closed) return;
      clearConnectFlow();
    };

    window.addEventListener("focus", syncPopupState);
    window.addEventListener("pageshow", syncPopupState);
    window.addEventListener("beforeunload", clearConnectFlow);
    return () => {
      window.removeEventListener("focus", syncPopupState);
      window.removeEventListener("pageshow", syncPopupState);
      window.removeEventListener("beforeunload", clearConnectFlow);
      clearConnectFlow();
    };
  }, []);

  // Initial load for the integrations page. Without this, the spinner never
  // resolves on a fresh visit because `loading` starts true and no fetch runs.
  useEffect(() => {
    void loadAll();
    void loadReadiness();
  }, [workspaceId]);

  // R3c — read the plan for the Pro gate. On any error, plan stays null → the
  // UI shows the upsell (never the submit form); the backend re-checks anyway.
  useEffect(() => {
    fetch(`/api/billing/usage?workspace_id=${encodeURIComponent(workspaceId)}`, { credentials: "include" })
      .then(r => r.json()).then(u => setPlan(u?.plan || "trial")).catch(() => {});
  }, [workspaceId]);

  // Persist the in-progress connector-setup builder to localStorage (debounced,
  // mirrors the intake-draft autosave) so navigating away / reloading the hub no
  // longer wipes it. An empty draft removes the key (see saveConnectorDraft).
  useEffect(() => {
    const handle = setTimeout(() => {
      saveConnectorDraft(workspaceId, { acceptedApps, appDraft });
    }, 600);
    return () => clearTimeout(handle);
  }, [acceptedApps, appDraft, workspaceId]);

  // `preserveNeedsSetup` = a provider that was JUST marked needs_setup this load
  // (a genuine no-managed-auth app the user clicked) — don't let the stale-cache
  // clear below wipe a flag we legitimately earned this session.
  async function loadAll(preserveNeedsSetup?: string) {
    setLoading(true);
    setLoadError(false);
    try {
      const [catalogRes, connRes, sourceRes] = await Promise.all([
        fetch("/api/mcp/catalog"),
        fetch(`/api/mcp/connections?workspace_id=${workspaceId}`),
        fetch(`/api/mcp/data-sources?workspace_id=${workspaceId}`),
      ]);
      // SF-2 — a non-2xx still returns valid JSON (e.g. {"error":"missing_workspace"}),
      // so without this check connections/sources come back undefined, collapse to [],
      // and the tiles render a silent, confident 0. Fail loudly instead.
      const failed = [
        !catalogRes.ok ? `catalog ${catalogRes.status}` : null,
        !connRes.ok ? `connections ${connRes.status}` : null,
        !sourceRes.ok ? `data-sources ${sourceRes.status}` : null,
      ].filter(Boolean);
      if (failed.length) throw new Error(`connector load failed: ${failed.join(", ")}`);
      const catalogData = await catalogRes.json();
      const connData = await connRes.json();
      const sourceData = await sourceRes.json();
      const providers: MCPProvider[] = catalogData.providers || [];
      setCatalog(providers);
      setComposioResults(sortFeaturedFirst(providers.filter((p: MCPProvider) => p.auth_provider === "composio")));
      setComposioError(catalogData.composio_error || null);
      // Server truth wins over stale cache: clear needs_setup for every app the
      // server now says is connectable, except one just re-marked this load.
      clearNeedsSetup(
        providers
          .filter((p: MCPProvider) => p.connectable && p.provider !== preserveNeedsSetup)
          .map((p: MCPProvider) => p.provider),
      );
      setConnections(connData.connections || []);
      setLiveSources(sourceData.sources || []);
      return connData.connections || [];
    } catch (err) {
      console.error("[IntegrationHub] load failed:", err);
      setLoadError(true);
      setStatusMessage("Couldn't load connectors — check your connection and retry.");
      return [];
    } finally {
      setLoading(false);
    }
  }

  async function loadReadiness(smokeProvider?: string) {
    try {
      const url = new URL("/api/connectors/readiness", window.location.origin);
      url.searchParams.set("workspace_id", workspaceId);
      if (smokeProvider) url.searchParams.set("smoke_provider", smokeProvider);
      const readinessRes = await fetch(url.toString());
      const readinessJson = await readinessRes.json().catch(() => null);
      if (!readinessRes.ok || !readinessJson) return;
      setReadiness(Array.isArray(readinessJson?.rows) ? readinessJson : null);
    } catch (err) {
      console.error("[IntegrationHub] readiness load failed:", err);
    }
  }

  async function smokeTestConnector(provider: string) {
    setSmokingProvider(provider);
    try {
      const res = await fetch(`/api/connectors/readiness?workspace_id=${encodeURIComponent(workspaceId)}&smoke_provider=${encodeURIComponent(provider)}`);
      const json = await res.json().catch(() => null);
      if (!res.ok || !json) {
        throw new Error(json?.error || `HTTP ${res.status}`);
      }
      setReadiness(json);
      const row = Array.isArray(json.rows) ? json.rows.find((r: ConnectorReadinessRow) => r.provider === provider) : null;
      if (row?.smoke_tested) {
        setStatusMessage(`✓ Smoke test passed for ${row.label}${row.smoke_tool ? ` via ${row.smoke_tool}` : ""}.`);
        void loadReadiness(provider);
      } else {
        setStatusMessage(`✗ Smoke test did not pass for ${row?.label || provider}${row?.failure_reason ? `: ${row.failure_reason}` : ""}.`);
      }
      setTimeout(() => setStatusMessage(null), 6000);
    } catch (err: any) {
      setStatusMessage(`✗ Smoke test failed for ${provider}: ${err?.message || "unknown error"}`);
      setTimeout(() => setStatusMessage(null), 6000);
    } finally {
      setSmokingProvider(null);
    }
  }

  function handleConnect(provider: string) {
    // Redirect to OAuth start; backend redirects to provider; provider redirects back.
    const authProvider = authProviderForConnector(provider, catalog.find((tool) => tool.provider === provider));
    if (!authProvider) {
      setStatusMessage(`No OAuth route is configured for ${provider}. Ask The Operator to add this connector first.`);
      setTimeout(() => setStatusMessage(null), 5000);
      return;
    }
    if (connectingProvider || connectInFlightRef.current) return;
    connectInFlightRef.current = provider;
    setConnectingProvider(provider);
    const oauthUrl = `/api/oauth/${encodeURIComponent(authProvider)}/start?provider=${encodeURIComponent(provider)}&workspace_id=${encodeURIComponent(workspaceId)}&return_screen=${encodeURIComponent(RETURN_SCREEN)}&return_mode=popup`;
    const popup = window.open("about:blank", "sloe-composio-connect", "popup=yes,width=560,height=760");
    if (popup) {
      connectPopupRef.current = popup;
      try {
        popup.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Connecting…</title><style>body{font-family:system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif;display:grid;place-items:center;min-height:100vh;margin:0;color:#0f172a;background:#fff}</style></head><body><div>Connecting your app…</div></body></html>`);
        popup.document.close();
      } catch {}
      popup.location.href = oauthUrl;
      popup.focus();
      if (connectTimeoutRef.current) window.clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = window.setTimeout(() => {
        try {
          if (connectPopupRef.current && !connectPopupRef.current.closed) connectPopupRef.current.close();
        } catch {}
        connectPopupRef.current = null;
        connectInFlightRef.current = null;
        setConnectingProvider(null);
      }, 120000);
      return;
    }
    window.location.href = oauthUrl;
  }

  // Live "will map / won't map" feedback: when a screen is picked in the RESO
  // modal, ask the server which RESO concepts bind to that screen's fields.
  useEffect(() => {
    if (!resoModalOpen || !resoScreenId) { setResoMapped([]); setResoUnmapped([]); return; }
    let cancelled = false;
    fetch("/api/connectors/reso/preview-mapping", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ workspace_id: workspaceId, screen_id: resoScreenId }),
    })
      .then((r) => r.json())
      .then((j) => {
        if (cancelled || !j?.ok) return;
        setResoMapped(j.mapped || []);
        setResoUnmapped(j.unmapped || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [resoModalOpen, resoScreenId, workspaceId]);

  // RESO is non-OAuth: paste base_url + token (no redirect). Parallel to
  // handleConnect — that one is OAuth-redirect-only and stays untouched.
  async function handleConnectReso() {
    const base_url = resoBaseUrl.trim();
    const token = resoToken.trim();
    const screen_id = resoScreenId.trim();
    if (!/^https?:\/\//.test(base_url) || !token) {
      setStatusMessage("Enter a valid RESO base URL (http[s]://…) and token.");
      setTimeout(() => setStatusMessage(null), 5000);
      return;
    }
    setResoSubmitting(true);
    try {
      const res = await fetch("/api/connectors/reso/connect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workspace_id: workspaceId, base_url, token, screen_id: screen_id || null }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || json?.ok === false) {
        setResoMapError(json?.error || `HTTP ${res.status}`); // inline in modal; keep it open
        return;
      }
      setResoModalOpen(false);
      setResoBaseUrl(""); setResoToken(""); setResoScreenId("");
      setStatusMessage("✓ RESO feed connected.");
      await loadAll();
      setTimeout(() => setStatusMessage(null), 4000);
    } catch (err: any) {
      setStatusMessage(`Could not connect RESO: ${err?.message || "unknown error"}`);
      setTimeout(() => setStatusMessage(null), 6000);
    } finally {
      setResoSubmitting(false);
    }
  }

  const readyRows = readiness?.rows?.filter((row) => row.usable) || [];
  const readySet = new Set(readyRows.map((item: any) => item.provider));
  const connectedConnections = connections.filter((conn) => conn.status === "connected");
  const connectedSet = new Set(connectedConnections.map((item: any) => item.provider));
  const optimisticConnection = justConnected && !connectedSet.has(justConnected)
    ? [{
      id: `pending-${justConnected}`,
      provider: justConnected,
      status: "connected",
      scopes: [],
      last_synced_at: new Date().toISOString(),
      expires_at: null,
      created_at: new Date().toISOString(),
      config: { optimistic: true },
    } as any]
    : [];
  const visibleConnections = [...connectedConnections, ...optimisticConnection]
    .sort((a, b) => Number(readySet.has(b.provider)) - Number(readySet.has(a.provider)));

  // Connectable catalog entries that aren't yet connected — surfaced as a
  // small, curated "Available" section above the Composio Browse catalog.
  const nativeCatalog = catalog.filter(
    t => t.connectable && !connectedSet.has(t.provider) && t.auth_provider !== "composio",
  );
  // Labels for the just-connected banner: `.full` in the headline, `.short` in
  // the chat CTA so the prompt reads cleanly (raw slug → friendly override).
  const justConnectedLabel = justConnected
    ? providerLabel(justConnected, catalog.find(p => p.provider === justConnected)?.name || justConnected)
    : null;

  function requestPermission(tool: MCPProvider) {
    if (!tool.connectable || connectedSet.has(tool.provider)) return;
    setPermissionTool(tool);
  }

  // Mark a Composio app as "needs setup" (no managed auth), persisted with
  // a 7-day TTL so a temporary gap self-clears and re-checks.
  function markNeedsSetup(provider: string) {
    try {
      const raw = JSON.parse(localStorage.getItem(`sloe.needs_setup.${workspaceId}`) || "{}");
      raw[provider] = Date.now() + NEEDS_SETUP_TTL_MS;
      localStorage.setItem(`sloe.needs_setup.${workspaceId}`, JSON.stringify(raw));
    } catch {}
    setNeedsSetup(prev => new Set(prev).add(provider));
  }

  // Drop cached needs_setup flags for apps the SERVER now reports connectable —
  // fresh server truth overrides stale client cache, so a user isn't trapped on
  // a grey tile that a pre-fix deploy wrote (e.g. Notion greyed before the
  // connect path was repaired). Clears both the live Set and localStorage.
  function clearNeedsSetup(providers: string[]) {
    if (providers.length === 0) return;
    const drop = new Set(providers);
    setNeedsSetup(prev => {
      if (![...drop].some(p => prev.has(p))) return prev; // no-op → keep reference
      const next = new Set(prev);
      for (const p of drop) next.delete(p);
      return next;
    });
    try {
      const raw = JSON.parse(localStorage.getItem(`sloe.needs_setup.${workspaceId}`) || "{}");
      let changed = false;
      for (const p of drop) if (p in raw) { delete raw[p]; changed = true; }
      if (changed) localStorage.setItem(`sloe.needs_setup.${workspaceId}`, JSON.stringify(raw));
    } catch {}
  }

  // R3c — submit a Pro-gated connector request (lands in support_requests +
  // emails the connector-request inbox via the R2 backend).
  async function submitConnectorRequest() {
    const app = submitApp.trim();
    if (!app) return;
    setSubmitting(true);
    try {
      const message = submitNote.trim() ? `${app} — ${submitNote.trim()}` : app;
      const res = await fetch("/api/support/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ kind: "connector_request", workspace_id: workspaceId, current_screen: "integrations", message }),
      });
      if (res.ok) {
        setSubmitDone(true); setSubmitOpen(false); setSubmitApp(""); setSubmitNote("");
      } else {
        const j = await res.json().catch(() => ({}));
        setStatusMessage(j?.error === "pro_required" ? "Requesting apps is a Pro feature." : "Couldn't submit — try again.");
        setTimeout(() => setStatusMessage(null), 5000);
      }
    } catch {
      setStatusMessage("Couldn't submit — try again.");
      setTimeout(() => setStatusMessage(null), 5000);
    } finally {
      setSubmitting(false);
    }
  }

  // R3c — the upsell button drives the REAL Stripe checkout (the agent can't
  // initiate an upgrade). Mirrors PlanComparison's startCheckout.
  async function startProCheckout() {
    setCheckingOut(true);
    try {
      const res = await fetch("/api/billing/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ os_id: workspaceId, plan: "pro" }),
      });
      const j = await res.json().catch(() => ({}));
      if (res.ok && j?.url) { window.location.href = j.url; return; }
      setStatusMessage(j?.error ? `Could not start checkout: ${j.error}` : "Could not start checkout.");
      setTimeout(() => setStatusMessage(null), 5000);
    } catch {
      setStatusMessage("Could not start checkout.");
      setTimeout(() => setStatusMessage(null), 5000);
    } finally {
      setCheckingOut(false);
    }
  }

  function requestConnector(tool: MCPProvider) {
    const prompt = buildConnectorRequestPrompt(tool);

    if (onAskOperator) {
      onAskOperator(prompt);
      return;
    }
    setStatusMessage(`Ask The Operator to request the ${tool.name} connector.`);
    setTimeout(() => setStatusMessage(null), 5000);
  }

  async function disconnectConnector(provider: string, label: string) {
    setDisconnectingProvider(provider);
    try {
      let res: Response;
      if (provider === "reso") {
        // RESO is non-OAuth → its own delete endpoint, not the OAuth disconnect.
        res = await fetch("/api/connectors/reso/disconnect", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ workspace_id: workspaceId }),
        });
      } else {
        const authProvider = authProviderForConnector(provider, catalog.find((tool) => tool.provider === provider));
        if (!authProvider) throw new Error(`No OAuth route is configured for ${provider}`);
        res = await fetch(`/api/oauth/${encodeURIComponent(authProvider)}/disconnect`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ workspace_id: workspaceId, provider }),
        });
      }
      const json = await res.json().catch(() => null);
      if (!res.ok || json?.ok === false) throw new Error(json?.error || `HTTP ${res.status}`);
      setPendingAccessAction(null);
      setJustConnected(null);
      window.history.replaceState({}, "", window.location.pathname);
      setStatusMessage(`Disconnected ${label}.`);
      await loadAll();
      void loadReadiness();
      setTimeout(() => setStatusMessage(null), 4000);
    } catch (err: any) {
      console.error("[IntegrationHub] disconnect failed:", err);
      setStatusMessage(`Could not disconnect ${label}: ${err?.message || "unknown error"}`);
      setTimeout(() => setStatusMessage(null), 6000);
    } finally {
      setDisconnectingProvider(null);
    }
  }

  async function confirmPendingAccessAction() {
    const action = pendingAccessAction;
    if (!action) return;

    if (action.kind === "disconnect") {
      await disconnectConnector(action.provider, action.label);
      return;
    }

    const permission = action.permission;
    setRevokingPermissionId(permission.id);
    try {
      const res = await fetch("/api/desktop-agent/permissions/revoke", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workspace_id: workspaceId, permission_id: permission.id }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || json?.ok === false) throw new Error(json?.error || `HTTP ${res.status}`);
      setPendingAccessAction(null);
      setStatusMessage(`Revoked local access: ${permission.label}`);
      await loadAll();
      setTimeout(() => setStatusMessage(null), 4000);
    } catch (err: any) {
      console.error("[IntegrationHub] desktop permission revoke failed:", err);
      setStatusMessage(`Could not revoke access: ${err?.message || "unknown error"}`);
      setTimeout(() => setStatusMessage(null), 6000);
    } finally {
      setRevokingPermissionId(null);
    }
  }

  // Client-side filter over the loaded Composio catalog — instant search.
  const browseQuery = searchQuery.trim().toLowerCase();
  const browseFiltered = browseQuery
    ? composioResults.filter(t =>
        t.name.toLowerCase().includes(browseQuery) ||
        t.provider.toLowerCase().includes(browseQuery) ||
        (t.description || "").toLowerCase().includes(browseQuery))
    : composioResults;
  const browseSections = useMemo(() => {
    const byCategory = new Map<string, MCPProvider[]>();
    for (const tool of browseFiltered) {
      const category = tool.category || "custom";
      const list = byCategory.get(category) || [];
      list.push(tool);
      byCategory.set(category, list);
    }
    const ordered = [
      ...CATEGORY_ORDER,
      ...Array.from(byCategory.keys()).filter((category) => !CATEGORY_ORDER.includes(category as any)).sort(),
    ];
    return ordered
      .map((category) => ({
        category,
        label: CATEGORY_LABELS[category] || category.replace(/[_-]+/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
        tools: (byCategory.get(category) || []).sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .filter((section) => section.tools.length > 0);
  }, [browseFiltered]);
  const categoryTabs = useMemo(
    () => browseSections.map((section) => ({
      category: section.category,
      label: section.label,
      count: section.tools.length,
    })),
    [browseSections],
  );
  const { curatedTabs, moreTabs } = useMemo(() => {
    const curated: typeof categoryTabs = [];
    const more: typeof categoryTabs = [];
    for (const tab of categoryTabs) {
      if (tab.category in CATEGORY_LABELS) {
        curated.push(tab);
      } else {
        more.push(tab);
      }
    }
    return { curatedTabs: curated, moreTabs: more };
  }, [categoryTabs]);
  const selectedSection = selectedCategory === "all"
    ? null
    : browseSections.find((section) => section.category === selectedCategory) || null;
  const featuredTools = browseFiltered.slice(0, FEATURED_COMPOSIO_PROVIDERS.length);
  const hasActiveCloudConnections = connections.length > 0;
  const hasActiveLiveSources = liveSources.length > 0;
  const hasActiveSources = hasActiveCloudConnections || hasActiveLiveSources;
  const connectedCount = visibleConnections.length;
  const readyForAgentCount = readyRows.length;
  const repairCount = readiness?.rows ? readiness.missing_count : connections.filter((conn) => connectorRepairState(conn).needsRepair).length;
  const liveSourceCount = liveSources.length;
  // SF-2 — a failed load must not read as a real zero. When the request behind a
  // tile failed, show "—" and let the load-failure banner explain why. Readiness
  // is tracked separately: loadReadiness returns silently on a non-ok response,
  // which would otherwise leave "Ready for agent use" reading a confident 0.
  const countsUnavailable = loadError;
  const readyUnavailable = loadError || !readiness?.rows;

  function sourceLabel(source: LiveDataSource): string {
    if (source.source_name) return source.source_name;
    if (source.provider === "google_sheets") return "Connected sheet";
    if (source.provider === "google_drive_csv") return "CSV file";
    return source.provider.replace(/[_-]+/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  }

  function formatDate(value: string | null): string {
    if (!value) return "Just now";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Recently";
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }

  function relTime(value: string | null): string {
    if (!value) return "never";
    const ms = Date.now() - new Date(value).getTime();
    if (!Number.isFinite(ms) || ms < 0) return "just now";
    const m = Math.floor(ms / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }

  function syncPrompt(source: LiveDataSource): string {
    const label = sourceLabel(source);
    const screen = source.screen_labels[0] || "the relevant screen";
    const screenId = source.screen_ids?.[0];
    if (source.provider === "google_drive_csv") {
      return `Sync ${label} into ${screen}${screenId ? ` (screen_id "${screenId}")` : ""}. Use the CSV import tool with file_id "${source.source_id}"${screenId ? ` and screen_id "${screenId}"` : " and ask me which screen_id to use before calling the tool"}. Pass source_name "${label}"${source.web_url ? ` and source_url "${source.web_url}"` : ""}. Tell me how many rows were inserted or updated.`;
    }
    return `Sync ${label} into ${screen}${screenId ? ` (screen_id "${screenId}")` : ""}. Use the sheet import tool with spreadsheet id "${source.source_id}"${source.range ? `, range "${source.range}",` : ","}${screenId ? ` and screen_id "${screenId}"` : " and ask me which screen_id to use before calling the tool"}. Pass source_name "${label}"${source.web_url ? ` and source_url "${source.web_url}"` : ""}. Tell me how many rows were inserted or updated.`;
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-slate-500 py-20">
        <Loader2 className="w-8 h-8 animate-spin mb-4" />
        <p>Loading integrations...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-light text-slate-900 flex items-center gap-3 mb-2">
          <Plug className="text-violet-500 w-8 h-8" /> Integrations
        </h1>
        <p className="text-slate-500">
          Connect the apps your agent should use. Search the catalog below, connect one, and it's available to your agent immediately.
        </p>
        {hasActiveSources && (
          <button
            type="button"
            onClick={() => setResoModalOpen(true)}
            className="mt-4 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
          >
            <Plus className="w-4 h-4" /> Other source
          </button>
        )}
      </div>

      <div className="mb-8 grid gap-3 md:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">Connected</div>
          <div className="mt-2 text-2xl font-semibold text-slate-900">{countsUnavailable ? "—" : connectedCount}</div>
          <div className="mt-1 text-sm text-slate-500">Apps that have actually completed auth.</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">Ready for agent use</div>
          <div className="mt-2 text-2xl font-semibold text-slate-900">{readyUnavailable ? "—" : readyForAgentCount}</div>
          <div className="mt-1 text-sm text-slate-500">Connected, scoped, tools exposed, and smoke tested.</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">Needs repair</div>
          <div className="mt-2 text-2xl font-semibold text-slate-900">{countsUnavailable ? "—" : repairCount}</div>
          <div className="mt-1 text-sm text-slate-500">Reconnect or approve access before an agent relies on it.</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">Live imports</div>
          <div className="mt-2 text-2xl font-semibold text-slate-900">{countsUnavailable ? "—" : liveSourceCount}</div>
          <div className="mt-1 text-sm text-slate-500">Sheets and files already mapped into workspace screens.</div>
        </div>
      </div>

      {/* Connector readiness card removed — the five-pill grid dominated the
          screen for no decision the user could act on. `readiness` state stays:
          repairCount and readyUnavailable above both read it, and the Smoke test
          action moved onto each connected integration card below. */}

      {/* Status banner */}
      {statusMessage && (
        <div className={`mb-6 px-4 py-3 rounded-lg text-sm font-medium ${
          statusMessage.startsWith("✓") || statusMessage.startsWith("Disconnected") || statusMessage.startsWith("Revoked")
            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
            : "bg-red-50 text-red-700 border border-red-200"
        }`}>
          {statusMessage}
        </div>
      )}

      {/* R3b / UX — if OAuth just returned success, show an immediate green card
          while the live connections fetch catches up. The real DB-backed card
          will replace it as soon as /api/mcp/connections resolves. */}
      {justConnected && !connectedSet.has(justConnected) && (
        <div className="mb-10">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">
            Connection saved
          </h2>
          <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4 shadow-sm">
            <div className="flex items-start gap-4">
              <ConnectorIcon
                icon={catalog.find(p => p.provider === justConnected)?.icon}
                name={justConnectedLabel?.full || justConnected}
                color={catalog.find(p => p.provider === justConnected)?.color}
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 text-sm truncate">{justConnectedLabel?.full || justConnected}</div>
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                  <AlertTriangle className="h-3 w-3" />
                  Finishing connection
                </div>
              </div>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Composio is finishing the account verification and first agent tool call.
            </p>
          </div>
        </div>
      )}

      {/* R3b feature B — the connection is already confirmed; this button is
          for USING it, not verifying it. */}
      {justConnected && onAskOperator && (
        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center justify-between gap-4">
          <div className="text-sm text-emerald-800">
            <span className="font-semibold">{justConnectedLabel?.full} is ready.</span> Try the connection with the Operator.
          </div>
          <div className="shrink-0 flex items-center gap-2">
            <button
              type="button"
              onClick={() => void disconnectConnector(justConnected, justConnectedLabel?.full || justConnected)}
              disabled={disconnectingProvider === justConnected}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              {disconnectingProvider === justConnected && <Loader2 className="h-4 w-4 animate-spin" />}
              Disconnect
            </button>
            <button
              type="button"
              onClick={() => onAskOperator(`Use my connected ${justConnectedLabel?.full || justConnected} account and show me the latest items you can access.`)}
              className="shrink-0 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium py-2 px-3 rounded-lg"
            >
              <MessageSquare size={14} /> Try with Operator →
            </button>
          </div>
        </div>
      )}

      {/* SF-2 — load-failure state (distinct from an empty catalog) with retry */}
      {loadError && (
        <div className="mb-6 px-4 py-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm flex items-center justify-between gap-3">
          <span>We couldn't load your connectors just now — this is a hiccup on our end, not an empty list.</span>
          <button
            onClick={() => loadAll()}
            className="shrink-0 px-3 py-1.5 rounded-md bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {pendingAccessAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start gap-4 border-b border-slate-100 p-5">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-50">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-slate-900">
                  {pendingAccessAction.kind === "disconnect" ? "Disconnect integration?" : "Revoke local access?"}
                </h2>
                <p className="mt-1 text-sm leading-relaxed text-slate-500">
                  {pendingAccessAction.kind === "disconnect"
                    ? `${pendingAccessAction.label} will stop feeding live data to the Operator, Analyst, and any screens that rely on it.`
                    : `Agents will no longer be able to use "${pendingAccessAction.permission.label}" through the approved local access path.`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPendingAccessAction(null)}
                className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close confirmation"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm leading-relaxed text-red-800">
                This does not delete your Agentic Operating System or records. It only removes this approved data path. You can reconnect it later from Integrations.
              </div>
            </div>
            <div className="flex flex-col gap-2 border-t border-slate-100 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setPendingAccessAction(null)}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100"
              >
                Keep access
              </button>
              <button
                type="button"
                onClick={confirmPendingAccessAction}
                disabled={
                  pendingAccessAction.kind === "disconnect"
                    ? disconnectingProvider === pendingAccessAction.provider
                    : revokingPermissionId === pendingAccessAction.permission.id
                }
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-60"
              >
                {(pendingAccessAction.kind === "disconnect"
                  ? disconnectingProvider === pendingAccessAction.provider
                  : revokingPermissionId === pendingAccessAction.permission.id) && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                {pendingAccessAction.kind === "disconnect" ? "Disconnect" : "Revoke access"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connect RESO feed — non-OAuth, paste credentials (base_url + token). */}
      {resoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start gap-4 border-b border-slate-100 p-5">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-violet-50">
                <Plug className="h-5 w-5 text-violet-600" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-slate-900">Connect a RESO feed</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Paste your RESO Web API base URL and access token. Listings sync into the chosen screen.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setResoModalOpen(false)}
                className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="space-y-4 p-5">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">RESO base URL</label>
                <input
                  type="text"
                  value={resoBaseUrl}
                  onChange={(e) => setResoBaseUrl(e.target.value)}
                  placeholder="https://api.example.com/RESO/OData"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
                />
                <p className="mt-1 text-xs text-slate-400">The path BEFORE /Property.</p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Access token</label>
                <input
                  type="password"
                  value={resoToken}
                  onChange={(e) => setResoToken(e.target.value)}
                  placeholder="RESO Web API bearer token"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Target screen</label>
                <select
                  value={resoScreenId}
                  onChange={(e) => setResoScreenId(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
                >
                  <option value="">Select a screen…</option>
                  {(screens || []).map((s) => (
                    <option key={s.screen_id} value={s.screen_id}>{s.label || s.screen_id}</option>
                  ))}
                </select>
                {resoScreenId && (resoMapped.length > 0 || resoUnmapped.length > 0) && (
                  <div className="mt-2 text-xs">
                    {resoMapped.length > 0 && (
                      <p className="text-emerald-700">Will map: {resoMapped.join(", ")}</p>
                    )}
                    {resoUnmapped.length > 0 && (
                      <p className="mt-0.5 text-slate-400">Won't map (no matching field): {resoUnmapped.join(", ")}</p>
                    )}
                  </div>
                )}
              </div>
              {resoMapError && <p className="text-xs text-red-600">{resoMapError}</p>}
            </div>
            <div className="flex flex-col gap-2 border-t border-slate-100 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setResoModalOpen(false)}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { setResoMapError(null); handleConnectReso(); }}
                disabled={resoSubmitting || !resoScreenId.trim()}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-violet-700 disabled:opacity-60"
              >
                {resoSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Connect feed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permission confirmation — mirrors the Agentic Operating System promise: the user
          explicitly grants access before agents can read a connected system. */}
      {permissionTool && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4">
          <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5 text-violet-600" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-slate-900">Grant permission</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Allow the Agentic Operating System to connect {permissionTool.name} so your agents can work from live data.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPermissionTool(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-start gap-3">
                <ConnectorIcon icon={permissionTool.icon} name={permissionTool.name} color={permissionTool.color} className="w-10 h-10 rounded-lg" />
                <div>
                  <div className="font-medium text-slate-900 text-sm">{permissionTool.name}</div>
                  <div className="text-xs uppercase tracking-wide text-slate-500 mt-0.5">
                    {CATEGORY_LABELS[permissionTool.category] || permissionTool.category}
                  </div>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{permissionTool.description}</p>
                  {permissionTool.capabilities && permissionTool.capabilities.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {permissionTool.capabilities.map((capability) => (
                        <span
                          key={capability}
                          className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600"
                        >
                          {capability}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-5 grid gap-3">
                {[
                  "Operator can answer questions using approved data from this tool.",
                  "Analyst can surface risks, stale records, and next actions from connected data.",
                  "You can revoke this connection from Active Connections at any time.",
                ].map((line) => (
                  <div key={line} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    <span>{line}</span>
                  </div>
                ))}
              </div>

              {permissionTool.auth_provider === "composio" && (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-500">
                  <ShieldCheck className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <span>
                    When you continue you&rsquo;ll be securely redirected to{" "}
                    <span className="font-medium text-slate-600">Composio</span>, our
                    integration partner, to sign in and approve access. Your password is
                    never seen or stored by your OS &mdash; revoke anytime from Active
                    Connections.
                  </span>
                </div>
              )}
            </div>
            <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row sm:justify-end gap-2">
              <button
                type="button"
                onClick={() => setPermissionTool(null)}
                className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={() => handleConnect(permissionTool.provider)}
                disabled={connectingProvider !== null}
                className="px-4 py-2 rounded-lg bg-slate-900 text-sm font-semibold text-white hover:bg-slate-800 disabled:bg-slate-400 disabled:cursor-wait transition-colors"
              >
                {connectingProvider === permissionTool.provider ? "Connecting…" : "Grant permission"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connected integrations */}
      {visibleConnections.length > 0 && (
        <div className="mb-10">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">
            Connected integrations ({visibleConnections.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleConnections.map(conn => {
              const provider = catalog.find(p => p.provider === conn.provider);
              const repair = connectorRepairState(conn);
              const statusClasses = repair.severity === "ok"
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : repair.severity === "error"
                  ? "border-red-200 bg-red-50 text-red-700"
                  : "border-amber-200 bg-amber-50 text-amber-700";
              const iconClasses = repair.severity === "ok"
                ? "text-emerald-500"
                : repair.severity === "error"
                  ? "text-red-500"
                  : "text-amber-500";
              const connectedCardClasses = repair.severity === "ok"
                ? "border-emerald-200 bg-gradient-to-br from-emerald-50 to-white shadow-sm"
                : repair.severity === "error"
                  ? "border-red-200 bg-red-50"
                  : "border-amber-200 bg-amber-50";
              return (
                <div
                  key={conn.id}
                  className={`border rounded-xl p-4 group relative ${connectedCardClasses}`}
                >
                  {repair.severity === "ok" && (
                    <div className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                      <Check className="h-3 w-3" />
                      Ready Integration
                    </div>
                  )}
                  <div className="flex items-start gap-4">
                    <ConnectorIcon
                      icon={provider?.icon}
                      name={provider?.name || conn.provider}
                      color={provider?.color}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 text-sm truncate">{conn.provider === "reso" ? "RESO feed" : providerLabel(conn.provider, provider?.name || conn.provider).full}</div>
                      <div className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusClasses}`}>
                        {repair.severity === "ok" ? <Wifi className={`w-3 h-3 ${iconClasses}`} /> : <WifiOff className={`w-3 h-3 ${iconClasses}`} />}
                        {repair.label}
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{repair.detail}</p>
                  {conn.provider === "reso" && (
                    <p className="mt-2 text-xs text-slate-400">Last synced {relTime(conn.last_synced_at)}</p>
                  )}
                  {repair.missingCapabilities.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {repair.missingCapabilities.map((capability) => (
                        <span
                          key={capability}
                          className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700"
                        >
                          {capability}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {repair.needsRepair && repair.actionLabel && (
                      <button
                        type="button"
                        onClick={() => {
                          if (conn.provider === "reso") {
                            setResoBaseUrl(conn.config?.base_url || "");
                            setResoScreenId(conn.config?.screen_id || "");
                            setResoToken("");
                            setResoMapError(null);
                            setResoModalOpen(true);
                          } else {
                            handleConnect(conn.provider);
                          }
                        }}
                        disabled={connectingProvider !== null && connectingProvider !== conn.provider}
                        className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                          repair.severity === "error"
                            ? "bg-red-600 text-white hover:bg-red-700"
                            : "bg-amber-600 text-white hover:bg-amber-700"
                        } ${connectingProvider !== null && connectingProvider !== conn.provider ? "opacity-60 cursor-wait" : ""}`}
                        title={`Reconnect ${provider?.name || conn.provider} to repair agent access`}
                      >
                        <ShieldCheck className="w-3.5 h-3.5" />
                        {connectingProvider === conn.provider ? "Connecting…" : repair.actionLabel}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => smokeTestConnector(conn.provider)}
                      disabled={smokingProvider === conn.provider || !readiness?.rows?.find((row) => row.provider === conn.provider)?.tool_exposed}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      title={`Run a live tool call against ${provider?.name || conn.provider}`}
                    >
                      <ShieldCheck className="w-3.5 h-3.5" />
                      {smokingProvider === conn.provider ? "Testing…" : "Smoke test"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingAccessAction({ kind: "disconnect", provider: conn.provider, label: provider?.name || conn.provider })}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-500 hover:bg-red-50 hover:border-red-200 hover:text-red-700 transition-colors"
                      title={`Disconnect ${provider?.name || conn.provider}`}
                    >
                      <X size={13} />
                      Disconnect
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {liveSources.length > 0 && (
        <div className="mb-10">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">
            Live Data Sources ({liveSources.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {liveSources.map(source => (
              <div key={source.id} className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                    <Wifi className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-slate-900 text-sm">{sourceLabel(source)}</div>
                      <span className="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                        {source.row_count} {source.row_count === 1 ? "row" : "rows"}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                      Feeding {source.screen_labels.length > 0 ? source.screen_labels.join(", ") : "the Agentic Operating System"}
                      {source.range ? ` from ${source.range}` : ""}.
                    </p>
                    <div className="mt-2 text-xs text-slate-400">
                      Last imported {formatDate(source.last_imported_at)}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {onAskOperator && (source.provider === "google_sheets" || source.provider === "google_drive_csv") && (
                        <button
                          type="button"
                          onClick={() => onAskOperator(syncPrompt(source))}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors"
                        >
                          <Zap className="w-3.5 h-3.5" />
                          Sync with Operator
                        </button>
                      )}
                      {source.web_url && (
                        <a
                          href={source.web_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Open source
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available — connectable catalog entries surfaced above the full Composio
          browse list. */}
      {nativeCatalog.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">Available</h2>
          <p className="text-xs text-slate-400 mb-4">Connected directly by Sloe. Grant access once and your agent can use it right away.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {nativeCatalog.map(tool => (
              <div key={tool.id} className="bg-white border border-slate-200 rounded-xl p-5 hover:border-violet-300 hover:shadow-md transition-all">
                <div className="flex items-start gap-4">
                  <ConnectorIcon icon={tool.icon} name={tool.name} color={tool.color} className="shadow-sm" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 text-sm">{providerLabel(tool.provider, tool.name).full}</div>
                    <div className="text-xs text-slate-500 mt-0.5 uppercase tracking-wide">{CATEGORY_LABELS[tool.category] || tool.category}</div>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mt-3 mb-4 leading-relaxed">{tool.description}</p>
                <button
                  type="button"
                  onClick={() => handleConnect(tool.provider)}
                  disabled={connectingProvider !== null}
                  className="w-full bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 disabled:cursor-wait text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-all text-sm"
                >
                  {connectingProvider === tool.provider ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  {connectingProvider === tool.provider ? "Connecting…" : "Connect"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Browse apps — Composio, live server-side search */}
      {SHOW_CONNECTOR_CATALOG && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">Browse apps</h2>
          <div className="relative mb-2">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search apps…"
              className="w-full rounded-2xl border border-slate-200 bg-white py-3.5 pl-11 pr-4 text-base text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-violet-500 focus:outline-none focus:ring-4 focus:ring-violet-500/10 transition-all"
            />
          </div>
          <p className="mb-4 text-xs text-slate-400">Connecting sends you to Composio to sign in and approve access — your password is never stored by your OS, and you can revoke anytime.</p>
          <div className="mb-4 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setSelectedCategory("all")}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-all ${
                  selectedCategory === "all"
                    ? "border-violet-300 bg-violet-50 text-violet-700"
                    : "border-slate-200 bg-white text-slate-600 hover:border-violet-300 hover:text-violet-700"
                }`}
              >
                All apps
                <span className="text-xs text-slate-400">{browseFiltered.length}</span>
              </button>
              {curatedTabs.map((tab) => (
                <button
                  key={tab.category}
                  type="button"
                  onClick={() => setSelectedCategory(tab.category)}
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-all ${
                    selectedCategory === tab.category
                      ? "border-violet-300 bg-violet-50 text-violet-700"
                      : "border-slate-200 bg-white text-slate-600 hover:border-violet-300 hover:text-violet-700"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
              {moreTabs.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowMoreCategories((prev) => !prev)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-all ${
                    showMoreCategories || moreTabs.some((t) => t.category === selectedCategory)
                      ? "border-violet-300 bg-violet-50 text-violet-700"
                      : "border-slate-200 bg-white text-slate-600 hover:border-violet-300 hover:text-violet-700"
                  }`}
                >
                  <span>More categories</span>
                  {showMoreCategories ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              )}
            </div>

            {showMoreCategories && moreTabs.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
                {moreTabs.map((tab) => (
                  <button
                    key={tab.category}
                    type="button"
                    onClick={() => setSelectedCategory(tab.category)}
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-all ${
                      selectedCategory === tab.category
                        ? "border-violet-300 bg-violet-50 text-violet-700"
                        : "border-slate-200 bg-white text-slate-600 hover:border-violet-300 hover:text-violet-700"
                    }`}
                  >
                    {tab.label}
                    <span className="text-xs text-slate-400">{tab.count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {composioError && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Live app catalog is unavailable ({composioError}). Try again shortly.
            </div>
          )}
          {browseSections.length > 0 ? (
            <div className="space-y-6">
              {selectedCategory === "all" ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Featured apps</h3>
                      <p className="text-xs text-slate-400">Quick picks from the full catalog</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                    {featuredTools.map(tool => {
                      const isConnected = connectedSet.has(tool.provider);
                      const isReady = readySet.has(tool.provider);
                      const needs = needsSetup.has(tool.provider);
                      return (
                        <div key={tool.id} className={`bg-white border rounded-2xl p-5 transition-all ${isConnected ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200 hover:border-violet-300 hover:shadow-md"}`}>
                          <div className="flex items-start gap-4">
                            <ConnectorIcon icon={tool.icon} name={tool.name} color={tool.color} className="shadow-sm" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-slate-900 text-sm">{tool.name}</div>
                              <div className="text-xs text-slate-500 mt-0.5 uppercase tracking-wide">{CATEGORY_LABELS[tool.category] || tool.category}</div>
                            </div>
                          </div>
                          <p className="text-sm text-slate-600 mt-3 mb-4 leading-relaxed">{tool.description}</p>
                          {isConnected ? (
                            <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium">
                              <Check size={16} />
                              {isReady ? "Ready" : "Connected"}
                            </div>
                          ) : needs ? (
                            <button
                              disabled
                              title="No managed auth for this app yet — connect isn't available. This clears automatically after 7 days and re-checks."
                              className="w-full bg-amber-50 text-amber-700 border border-amber-200 font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 text-sm cursor-not-allowed"
                            >
                              <AlertTriangle size={14} /> Needs setup
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleConnect(tool.provider)}
                              disabled={connectingProvider !== null}
                              className="w-full bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 disabled:cursor-wait text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-all text-sm"
                            >
                              {connectingProvider === tool.provider ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                              {connectingProvider === tool.provider ? "Connecting…" : "Connect"}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : selectedSection ? (
                <section className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">{selectedSection.label}</h3>
                      <p className="text-xs text-slate-400">{selectedSection.tools.length} app{selectedSection.tools.length === 1 ? "" : "s"} ready to connect</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {selectedSection.tools.map(tool => {
                      const isConnected = connectedSet.has(tool.provider);
                      const isReady = readySet.has(tool.provider);
                      const needs = needsSetup.has(tool.provider);
                      return (
                        <div key={tool.id} className={`bg-white border rounded-2xl p-5 transition-all ${isConnected ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200 hover:border-violet-300 hover:shadow-md"}`}>
                          <div className="flex items-start gap-4">
                            <ConnectorIcon icon={tool.icon} name={tool.name} color={tool.color} className="shadow-sm" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-slate-900 text-sm">{tool.name}</div>
                              <div className="text-xs text-slate-500 mt-0.5 uppercase tracking-wide">{CATEGORY_LABELS[tool.category] || tool.category}</div>
                            </div>
                          </div>
                          <p className="text-sm text-slate-600 mt-3 mb-4 leading-relaxed">{tool.description}</p>
                          {isConnected ? (
                            <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium">
                              <Check size={16} />
                              {isReady ? "Ready" : "Connected"}
                            </div>
                          ) : needs ? (
                            <button
                              disabled
                              title="No managed auth for this app yet — connect isn't available. This clears automatically after 7 days and re-checks."
                              className="w-full bg-amber-50 text-amber-700 border border-amber-200 font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 text-sm cursor-not-allowed"
                            >
                              <AlertTriangle size={14} /> Needs setup
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleConnect(tool.provider)}
                              disabled={connectingProvider !== null}
                              className="w-full bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 disabled:cursor-wait text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-all text-sm"
                            >
                              {connectingProvider === tool.provider ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                              {connectingProvider === tool.provider ? "Connecting…" : "Connect"}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              ) : (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-600">
                  No apps in this category yet. Try another filter or search again.
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-10 text-slate-500 text-sm">
              {searchQuery.trim() ? `No apps match “${searchQuery}”.` : "No apps to show."}
            </div>
          )}

          {/* R3c feature A — Pro-gated "Submit request"; non-Pro sees an upsell
              (not a wall) whose button drives the REAL Stripe checkout. */}
          <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-5">
            {isProPlan(plan) ? (
              submitDone ? (
                <p className="text-sm text-emerald-700">✓ Request received — we'll look at adding it.</p>
              ) : !submitOpen ? (
                <button onClick={() => setSubmitOpen(true)} className="text-sm font-medium text-violet-700 hover:text-violet-900">
                  Can't find your app? Submit a request →
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="text-sm font-semibold text-slate-900">Request an app</div>
                  <input
                    value={submitApp}
                    onChange={e => setSubmitApp(e.target.value)}
                    placeholder="App name (e.g. Pipedrive)"
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
                  />
                  <input
                    value={submitNote}
                    onChange={e => setSubmitNote(e.target.value)}
                    placeholder="What would you use it for? (optional)"
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
                  />
                  <div className="flex gap-2">
                    <button
                      disabled={submitting || !submitApp.trim()}
                      onClick={submitConnectorRequest}
                      className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-medium py-2 px-4 rounded-lg"
                    >
                      {submitting ? "Submitting…" : "Submit"}
                    </button>
                    <button onClick={() => setSubmitOpen(false)} className="text-sm text-slate-500 py-2 px-3">Cancel</button>
                  </div>
                </div>
              )
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">Requesting apps is a Pro feature.</span> Upgrade to ask us to add any app you need.
                </div>
                <button
                  onClick={startProCheckout}
                  disabled={checkingOut}
                  className="shrink-0 inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-medium py-2 px-3 rounded-lg"
                >
                  {checkingOut ? "Starting checkout…" : "Upgrade to Pro"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hint card */}
      <div className="mt-10 bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200 rounded-2xl p-6 flex items-start gap-4">
        <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center shrink-0">
          <Zap className="text-violet-600 w-5 h-5" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 text-sm mb-1">
            Every connection makes your Operator smarter
          </h3>
          <p className="text-slate-600 text-sm leading-relaxed">
            When you connect a tool, your Operator gains real-time access to that system's data via MCP. 
            Ask questions, request actions, and watch your Agentic Operating System propose new screens and workflows based on what it learns.
          </p>
        </div>
      </div>
    </div>
  );
}
